'use strict';

(() => {
  if (window.OluvieCartReturn) return;
  window.OluvieCartReturn = true;
  const lifetime = 30 * 60 * 1000;
  const fallbacks = new WeakMap();
  let cartRecord;
  let recordRead = false;
  let queued = false;

  function route(value) {
    try {
      const url = new URL(value, location.href);
      if (!['http:', 'https:'].includes(url.protocol) || url.origin !== location.origin || url.username || url.password) return null;
      const path = decodeURIComponent(url.pathname);
      if (/[\\\u0000-\u001f\u007f]/.test(path)) return null;
      const segments = path.split('/').filter(Boolean);
      if (segments.some(segment => /^(?:cart(?:\.js)?|admin|account|accounts|checkout|checkouts)$/i.test(segment))) {
        return { url, segments, restricted: true };
      }
      if (/^[a-z]{2}(?:-[a-z]{2})?$/i.test(segments[0] || '')) segments.shift();
      return { url, segments, restricted: false };
    } catch (_) { return null; }
  }

  function publicUrl(value) {
    const result = route(value);
    if (!result || result.restricted) return null;
    if (result.segments.length && !['products', 'collections', 'search', 'pages', 'blogs', 'policies'].includes(result.segments[0])) return null;
    return result.url;
  }

  function cartUrl(value) {
    const result = route(value);
    if (!result) return null;
    const segments = [...result.segments];
    if (/^[a-z]{2}(?:-[a-z]{2})?$/i.test(segments[0] || '')) segments.shift();
    return segments.length === 1 && segments[0] === 'cart' ? result.url : null;
  }

  function storageKey() {
    const id = window.Shopify?.theme?.id ?? new URL(location.href).searchParams.get('preview_theme_id');
    return /^\d+$/.test(String(id || '')) ? `oluvie:cart-return:v1:${location.origin}:${id}` : null;
  }

  function validRecord(record) {
    if (!record || !Number.isFinite(record.created) || !Number.isInteger(record.historyLength) || record.historyLength < 1) return null;
    const age = Date.now() - record.created;
    if (age < 0 || age > lifetime) return null;
    const source = typeof record.source === 'string' && publicUrl(record.source);
    const destination = typeof record.destination === 'string' && cartUrl(record.destination);
    if (!source || !destination || destination.href !== location.href) return null;
    return { ...record, source: source.href, destination: destination.href };
  }

  function readRecord() {
    if (!recordRead) {
      recordRead = true;
      try {
        const key = storageKey();
        if (key) cartRecord = JSON.parse(sessionStorage.getItem(key) || 'null');
      } catch (_) { cartRecord = null; }
    }
    return validRecord(cartRecord);
  }

  function referrerUrl() {
    return document.referrer ? publicUrl(document.referrer) : null;
  }

  function previousEntry(record) {
    if (!record || history.length !== record.historyLength + 1 || history.length < 2) return false;
    const expected = new URL(record.source);
    expected.hash = ''; // Browsers omit fragments from document.referrer.
    const referrer = referrerUrl();
    return Boolean(referrer && referrer.href === expected.href);
  }

  function fallback(link) {
    if (!fallbacks.has(link)) {
      const root = window.Shopify?.routes?.root || '/';
      const url = publicUrl(link.getAttribute('href')) || publicUrl(`${root}collections/all`) || new URL('/collections/all', location.origin);
      fallbacks.set(link, url.href);
    }
    return fallbacks.get(link);
  }

  function updateLink(link) {
    const original = fallback(link);
    const referrer = referrerUrl();
    let record = referrer ? readRecord() : null;
    if (record) {
      const expected = new URL(record.source);
      expected.hash = ''; // Preserve a stored fragment only when its public referrer matches.
      if (expected.href !== referrer.href) record = null;
    }
    link.href = record?.source || referrer?.href || original;
    return record;
  }

  function sync() {
    queued = false;
    if (!cartUrl(location.href)) return;
    for (const link of document.querySelectorAll('a[data-or-cart-back]')) updateLink(link);
  }

  function schedule() {
    if (queued) return;
    queued = true;
    Promise.resolve().then(sync);
  }

  function sameTabClick(event, link) {
    const target = link.getAttribute('target') || document.querySelector('base[target]')?.getAttribute('target') || '_self';
    return !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && !link.hasAttribute('download') && target.toLowerCase() === '_self';
  }

  document.addEventListener('click', event => {
    const link = event.target?.closest?.('a[href]');
    if (!link || !sameTabClick(event, link)) return;
    if (link.hasAttribute('data-or-cart-back') && cartUrl(location.href)) {
      const record = updateLink(link);
      if (previousEntry(record)) { event.preventDefault(); history.back(); }
      return;
    }
    const destination = cartUrl(link.href);
    const source = publicUrl(location.href);
    const key = storageKey();
    if (!destination || !source || !key) return;
    const record = { source: source.href, destination: destination.href, created: Date.now(), historyLength: history.length };
    // Wait until propagation completes so a cancelled click never records a departure.
    Promise.resolve().then(() => {
      if (event.defaultPrevented) return;
      try { sessionStorage.setItem(key, JSON.stringify(record)); } catch (_) {}
    });
  });

  function start() {
    new MutationObserver(records => {
      if (records.some(record => record.type === 'childList')) schedule();
    }).observe(document.body, { childList: true, subtree: true });
    sync();
  }

  window.addEventListener('pageshow', schedule);
  window.addEventListener('popstate', schedule);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
